import { useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@design-system';
import { useFormDefinition, useFormMetadata } from '@/features/quote-and-apply-builder';
import {
  mapFormQuestionsToDocusignTabs,
  type MappedDocusignTab,
} from '../utils/docusign-tab-mapper';
import { buildDiffRows, diffStatusConfig, type DiffRow } from '../utils/docusign-diff';
import { useDocusignTemplateTabs } from '../hooks/use-docusign-template-tabs';

interface DocusignTabsInspectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function InspectorTable({ rows }: { rows: MappedDocusignTab[] }) {
  if (rows.length === 0) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        No questions in this category.
      </p>
    );
  }

  return (
    <div className="border-border overflow-auto rounded border text-xs">
      <Table>
        <TableHeader className="bg-muted sticky top-0 z-10">
          <TableRow className="divide-x divide-border">
            <TableHead>Question Ref</TableHead>
            <TableHead>Question Title</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Tab Label(s)</TableHead>
            <TableHead>Section → Subsection</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(row => (
            <TableRow
              key={row.questionRef}
              className="divide-x divide-border"
            >
              <TableCell className="text-foreground w-56 whitespace-normal font-mono text-xs">
                {row.questionRef}
              </TableCell>
              <TableCell className="text-foreground w-64 whitespace-normal text-xs">
                {row.questionTitle}
                {row.hasComposite && (
                  <Badge
                    variant="secondary"
                    className="mt-0.5 ml-1 text-[10px]"
                  >
                    composite
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-foreground w-20 text-xs">{row.questionType}</TableCell>
              <TableCell className="w-72">
                <div className="flex flex-wrap gap-1">
                  {row.tabLabels.map(label => (
                    <Badge
                      key={label}
                      variant="secondary"
                      className="font-mono text-[10px] font-normal"
                    >
                      {label}
                    </Badge>
                  ))}
                </div>
              </TableCell>
              <TableCell className="text-foreground w-52 whitespace-normal text-xs">
                {row.sectionTitle}
                {row.subsectionTitle !== '—' && (
                  <>
                    <span className="text-muted-foreground mx-1">→</span>
                    {row.subsectionTitle}
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function DiffTable({ rows, search }: { rows: DiffRow[]; search: string }) {
  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      r =>
        r.label.toLowerCase().includes(q) ||
        r.questionTitle?.toLowerCase().includes(q) ||
        r.signer?.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const matched = filtered.filter(r => r.status === 'matched').length;
  const templateOnly = filtered.filter(r => r.status === 'template-only').length;
  const formOnly = filtered.filter(r => r.status === 'form-only').length;

  return (
    <div className="flex flex-col gap-3">
      <div className="text-muted-foreground flex gap-3 text-xs">
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-green-600" />
          {matched} matched
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-yellow-600" />
          {templateOnly} template-only (will be empty in DocuSign)
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-blue-600" />
          {formOnly} form-only (sent but no matching tab in template)
        </span>
      </div>

      <div className="border-border overflow-auto rounded border text-xs">
        <Table>
          <TableHeader className="bg-muted sticky top-0 z-10">
            <TableRow className="divide-x divide-border">
              <TableHead>Tab Label / Group Name</TableHead>
              <TableHead>Tab Type</TableHead>
              <TableHead>Question Title</TableHead>
              <TableHead>Signer Role</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((row, i) => {
              const cfg = diffStatusConfig[row.status];
              return (
                <TableRow
                  key={`${row.label}-${i}`}
                  className={`divide-x divide-border ${cfg.rowClass}`}
                >
                  <TableCell className="text-foreground font-mono">{row.label}</TableCell>
                  <TableCell className="text-muted-foreground">{row.tabType}</TableCell>
                  <TableCell className="text-foreground w-52 whitespace-normal">
                    {row.questionTitle ?? <span className="text-muted-foreground/40">—</span>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {row.signer ?? <span className="text-muted-foreground/40">—</span>}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={cfg.badgeVariant}
                      className="text-[10px]"
                    >
                      {cfg.label}
                    </Badge>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ─── Main dialog ──────────────────────────────────────────────────────────────

export function DocusignTabsInspectorDialog({
  open,
  onOpenChange,
}: DocusignTabsInspectorDialogProps) {
  const formDefinition = useFormDefinition();
  const formMetadata = useFormMetadata();
  const [search, setSearch] = useState('');

  const formId = formMetadata.formId;

  const {
    data: templateData,
    isLoading: isLoadingTemplate,
    error: templateError,
  } = useDocusignTemplateTabs({ formId, enabled: open });

  const allTabs = useMemo(() => mapFormQuestionsToDocusignTabs(formDefinition), [formDefinition]);

  const filtered = useMemo(() => {
    if (!search.trim()) return allTabs;
    const q = search.toLowerCase();
    return allTabs.filter(
      t =>
        t.questionRef.toLowerCase().includes(q) ||
        t.questionTitle.toLowerCase().includes(q) ||
        t.tabLabels.some(l => l.toLowerCase().includes(q)),
    );
  }, [allTabs, search]);

  const textTabs = filtered.filter(t => t.tabType === 'textTab');
  const checkboxTabs = filtered.filter(t => t.tabType === 'checkboxTab');
  const radioTabs = filtered.filter(t => t.tabType === 'radioGroupTab');
  const unmapped = filtered.filter(t => t.tabType === 'none');

  const diffRows = useMemo(() => {
    if (!templateData) return [];
    return buildDiffRows(allTabs, templateData.signers);
  }, [allTabs, templateData]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="max-w-7xl! flex max-h-[85vh] flex-col">
        <DialogHeader>
          <DialogTitle>DocuSign Tabs Inspector</DialogTitle>
        </DialogHeader>

        <Input
          placeholder="Search by question ref, title, tab label, or signer role…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="shrink-0"
        />

        <Tabs
          defaultValue="compare"
          className="flex flex-1 flex-col overflow-hidden"
        >
          <TabsList className="mb-2 shrink-0">
            <TabsTrigger value="compare">
              Template vs Form
              {templateData && (
                <Badge
                  variant="secondary"
                  className="ml-1.5"
                >
                  {diffRows.filter(r => r.status !== 'matched').length > 0
                    ? `${diffRows.filter(r => r.status !== 'matched').length} issues`
                    : '✓'}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="text">
              Text Tabs
              <Badge
                variant="secondary"
                className="ml-1.5"
              >
                {textTabs.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="checkbox">
              Checkbox Tabs
              <Badge
                variant="secondary"
                className="ml-1.5"
              >
                {checkboxTabs.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="radio">
              Radio Groups
              <Badge
                variant="secondary"
                className="ml-1.5"
              >
                {radioTabs.length}
              </Badge>
            </TabsTrigger>
            {unmapped.length > 0 && (
              <TabsTrigger value="none">
                Unmapped
                <Badge
                  variant="secondary"
                  className="ml-1.5"
                >
                  {unmapped.length}
                </Badge>
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent
            value="compare"
            className="flex-1 overflow-auto"
          >
            {isLoadingTemplate && (
              <div className="flex items-center justify-center py-12">
                <Spinner className="size-5" />
                <span className="text-muted-foreground ml-2 text-sm">
                  Loading template from DocuSign…
                </span>
              </div>
            )}
            {templateError && (
              <Alert variant="destructive">
                <AlertDescription>
                  Failed to load template tabs from DocuSign. Make sure this form has a DocuSign
                  template ID configured.
                </AlertDescription>
              </Alert>
            )}
            {templateData && (
              <DiffTable
                rows={diffRows}
                search={search}
              />
            )}
            {!isLoadingTemplate && !templateError && !templateData && (
              <p className="text-muted-foreground py-8 text-center text-sm">
                No template data loaded yet.
              </p>
            )}
          </TabsContent>

          <TabsContent
            value="text"
            className="flex-1 overflow-auto"
          >
            <InspectorTable rows={textTabs} />
          </TabsContent>

          <TabsContent
            value="checkbox"
            className="flex-1 overflow-auto"
          >
            <InspectorTable rows={checkboxTabs} />
          </TabsContent>

          <TabsContent
            value="radio"
            className="flex-1 overflow-auto"
          >
            <InspectorTable rows={radioTabs} />
          </TabsContent>

          {unmapped.length > 0 && (
            <TabsContent
              value="none"
              className="flex-1 overflow-auto"
            >
              <InspectorTable rows={unmapped} />
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
